import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import LinkExt from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import {
  Bold, Italic, List, ListOrdered, Link2, Heading2, Heading3,
  ImageIcon, Sparkles, Save, Eye, X, Monitor, ArrowLeft,
  Wand2, Quote, Type as TypeIcon, ChevronDown, Loader2, ExternalLink,
  CalendarClock, Star, Link as LinkIcon,
} from 'lucide-react';
import { RelationalPicker } from '../../portal/components/RelationalPicker';
import PexelsImagePicker from '../../portal/components/PexelsImagePicker';
import { pexelsifyUrl } from '../../utils/pexels';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Editor-side content normalizer — mirrors the public BlogPostPage logic.
 * Cleans up empty spacer paragraphs and leading duplicate <h1> so what saves
 * is what publishes. Spacing comes from CSS margins, not blank paragraphs.
 */
function normalizeContent(html, title) {
  if (!html) return '';
  let out = html;
  out = out.replace(/^\s*<h1[^>]*>([\s\S]*?)<\/h1>/i, (full, inner) => {
    const innerText = inner.replace(/<[^>]+>/g, '').trim();
    if (title && innerText && innerText.toLowerCase() === title.toLowerCase()) return '';
    return full;
  });
  // Unwrap <strong>/<b> inside headings so all article headings have consistent weight
  out = out.replace(/<(h[1-6])([^>]*)>([\s\S]*?)<\/\1>/gi, (full, tag, attrs, inner) => {
    const cleaned = inner.replace(/<\s*(strong|b)([^>]*)>([\s\S]*?)<\s*\/\s*\1\s*>/gi, '$3').trim();
    return `<${tag}${attrs}>${cleaned}</${tag}>`;
  });
  // Strip inline styles so the .vt-journal-prose CSS class fully governs appearance
  out = out.replace(/\sstyle\s*=\s*"[^"]*"/gi, '');
  out = out.replace(/\sstyle\s*=\s*'[^']*'/gi, '');
  out = out.replace(/<p[^>]*>\s*(?:<br\s*\/?>)?\s*<\/p>/gi, '');
  out = out.replace(/(<p[^>]*>\s*<\/p>\s*){2,}/gi, '');
  return out.trim();
}

function relativeTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const s = Math.round(diff / 1000);
  if (s < 5) return 'just now';
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return d.toLocaleDateString();
}

/**
 * Schedule auto-publish modal.
 *
 * Lets the advisor pick a date + time. We always show the user their LOCAL
 * timezone, but send UTC to the backend so the scheduler comparison is
 * timezone-stable. Date defaults to today + 1 day at 09:00 local.
 */
function ScheduleModal({ initialIso, currentStatus, onClose, onSchedule, onUnschedule, saving }) {
  // Convert UTC ISO → local "YYYY-MM-DD" + "HH:mm" for the two inputs
  const ymdHm = (iso) => {
    const base = iso ? new Date(iso) : (() => { const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(9, 0, 0, 0); return d; })();
    const pad = (n) => String(n).padStart(2, '0');
    return {
      d: `${base.getFullYear()}-${pad(base.getMonth() + 1)}-${pad(base.getDate())}`,
      t: `${pad(base.getHours())}:${pad(base.getMinutes())}`,
    };
  };
  const [{ d: initD, t: initT }] = useState(ymdHm(initialIso));
  const [date, setDate] = useState(initD);
  const [time, setTime] = useState(initT);

  const composedIso = (() => {
    if (!date || !time) return '';
    // Construct local datetime, convert to UTC ISO
    const local = new Date(`${date}T${time}:00`);
    if (Number.isNaN(local.getTime())) return '';
    return local.toISOString();
  })();
  const inPast = composedIso ? new Date(composedIso).getTime() < Date.now() : false;
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const presets = [
    { label: 'Tomorrow 9 AM', offset: () => { const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(9, 0, 0, 0); return d; } },
    { label: 'In 1 hour', offset: () => { const d = new Date(); d.setHours(d.getHours() + 1); d.setMinutes(0, 0, 0); return d; } },
    { label: 'Next Monday 8 AM', offset: () => { const d = new Date(); const day = d.getDay(); const add = ((1 - day + 7) % 7) || 7; d.setDate(d.getDate() + add); d.setHours(8, 0, 0, 0); return d; } },
  ];
  const applyPreset = (fn) => {
    const d = fn();
    const pad = (n) => String(n).padStart(2, '0');
    setDate(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`);
    setTime(`${pad(d.getHours())}:${pad(d.getMinutes())}`);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="journal-schedule-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-xl text-[#1A1A1A]">Auto-publish</h3>
          <button onClick={onClose} className="p-1 hover:bg-[#FAFAF8] rounded" type="button"><X size={16} /></button>
        </div>
        <p className="text-xs text-[#6B6B6B] mb-4">
          The entry will sit in <em>Scheduled</em> until this moment, then flip to <em>Published</em> automatically.
        </p>
        <div className="grid grid-cols-2 gap-2 mb-3">
          <div>
            <label className="block text-[11px] uppercase tracking-[0.15em] text-[#999] mb-1.5">Date</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full text-sm px-3 py-2 border border-[#E8E4DC] rounded outline-none focus:border-[#1A1A1A]"
              data-testid="schedule-date-input"
            />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.15em] text-[#999] mb-1.5">Time</label>
            <input
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              className="w-full text-sm px-3 py-2 border border-[#E8E4DC] rounded outline-none focus:border-[#1A1A1A]"
              data-testid="schedule-time-input"
            />
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5 mb-4">
          {presets.map((p) => (
            <button
              key={p.label}
              onClick={() => applyPreset(p.offset)}
              className="px-2.5 py-1 text-[11px] rounded-full border border-[#E8E4DC] text-[#6B6B6B] hover:border-[#1A1A1A] hover:text-[#1A1A1A] transition-colors"
              type="button"
              data-testid={`schedule-preset-${p.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className={`text-[12px] mb-4 px-3 py-2 rounded ${inPast ? 'bg-[#FCEAE0] text-[#8B4513]' : 'bg-[#FAFAF8] text-[#6B6B6B]'}`} data-testid="schedule-preview">
          {inPast ? 'That moment is already past — pick a future time.' : composedIso ? (
            <>Will publish on <strong className="text-[#1A1A1A]">{new Date(composedIso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}</strong> <span className="text-[#999]">({tz})</span></>
          ) : 'Pick a date and time.'}
        </div>
        <div className="flex justify-between items-center gap-2">
          {currentStatus === 'scheduled' && (
            <button
              onClick={onUnschedule}
              disabled={saving}
              className="text-[12px] text-[#C9622D] hover:underline disabled:opacity-50"
              type="button"
              data-testid="schedule-unschedule"
            >
              Clear schedule
            </button>
          )}
          <div className="flex gap-2 ml-auto">
            <button onClick={onClose} disabled={saving} className="px-4 py-2 text-sm border border-[#E8E4DC] rounded hover:border-[#1A1A1A]" type="button">Cancel</button>
            <button
              onClick={() => onSchedule(composedIso)}
              disabled={saving || !composedIso || inPast}
              className="px-4 py-2 text-sm bg-[#1A1A1A] text-white rounded hover:bg-[#333] disabled:opacity-50 inline-flex items-center gap-1.5"
              type="button"
              data-testid="schedule-confirm"
            >
              {saving ? <Loader2 size={12} className="animate-spin" /> : <CalendarClock size={12} />}
              {saving ? 'Scheduling…' : currentStatus === 'scheduled' ? 'Update schedule' : 'Schedule'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── AI actions that operate on the WHOLE article (write directly into editor) ──
const DOC_ACTIONS = [
  { key: 'improve_structure', label: 'Improve structure', hint: 'Add section headings, tighten paragraphs' },
  { key: 'format_web', label: 'Format for web', hint: 'Web-ready headings, paragraphs, pull-quote' },
  { key: 'more_luxury', label: 'More editorial / luxury', hint: 'Lift word choice, slow the rhythm' },
  { key: 'shorten', label: 'Shorten', hint: 'Tighten the whole piece' },
  { key: 'expand', label: 'Expand', hint: 'Add depth and imagery' },
  { key: 'add_intro', label: 'Add intro paragraph', hint: 'Prepend an opening scene-setter' },
  { key: 'add_conclusion', label: 'Add conclusion', hint: 'Append a quiet closing paragraph' },
];

// ── AI actions that operate on the CURRENT SELECTION ──
const SELECTION_ACTIONS = [
  { key: 'rewrite', label: 'Rewrite selection' },
  { key: 'shorten', label: 'Shorten selection' },
  { key: 'expand', label: 'Expand selection' },
  { key: 'more_editorial', label: 'More editorial' },
  { key: 'more_luxury', label: 'More luxury' },
  { key: 'improve', label: 'Improve clarity' },
  { key: 'fix_grammar', label: 'Fix grammar' },
];

// ── Editor toolbar (formatting only — AI lives in its own bar above) ──
function FormatToolbar({ editor, onInsertImage }) {
  if (!editor) return null;
  const btn = (active, onClick, children, testId, label) => (
    <button
      onClick={onClick}
      title={label}
      className={`p-1.5 rounded transition-colors ${active ? 'bg-[#1A1A1A] text-white' : 'text-[#6B6B6B] hover:text-[#1A1A1A] hover:bg-[#F0EDE6]'}`}
      data-testid={testId}
      type="button"
    >
      {children}
    </button>
  );
  return (
    <div className="flex items-center gap-1 p-2 border-b border-[#E8E4DC] bg-[#FAFAF8]" data-testid="editor-toolbar">
      {btn(editor.isActive('bold'), () => editor.chain().focus().toggleBold().run(), <Bold size={14} />, 'toolbar-bold', 'Bold')}
      {btn(editor.isActive('italic'), () => editor.chain().focus().toggleItalic().run(), <Italic size={14} />, 'toolbar-italic', 'Italic')}
      <span className="w-px h-4 bg-[#E8E4DC] mx-1" />
      {btn(editor.isActive('heading', { level: 2 }), () => editor.chain().focus().toggleHeading({ level: 2 }).run(), <Heading2 size={14} />, 'toolbar-h2', 'Heading 2')}
      {btn(editor.isActive('heading', { level: 3 }), () => editor.chain().focus().toggleHeading({ level: 3 }).run(), <Heading3 size={14} />, 'toolbar-h3', 'Heading 3')}
      {btn(editor.isActive('blockquote'), () => editor.chain().focus().toggleBlockquote().run(), <Quote size={14} />, 'toolbar-quote', 'Pull quote')}
      <span className="w-px h-4 bg-[#E8E4DC] mx-1" />
      {btn(editor.isActive('bulletList'), () => editor.chain().focus().toggleBulletList().run(), <List size={14} />, 'toolbar-ul', 'Bullet list')}
      {btn(editor.isActive('orderedList'), () => editor.chain().focus().toggleOrderedList().run(), <ListOrdered size={14} />, 'toolbar-ol', 'Numbered list')}
      <span className="w-px h-4 bg-[#E8E4DC] mx-1" />
      {btn(false, () => {
        const url = window.prompt('Enter URL');
        if (url) editor.chain().focus().setLink({ href: url }).run();
      }, <Link2 size={14} />, 'toolbar-link', 'Insert link')}
      {btn(false, onInsertImage, <ImageIcon size={14} />, 'toolbar-image', 'Insert image')}
    </div>
  );
}

export default function JournalEditor() {
  const { id } = useParams();
  const isNew = id === 'new';
  const navigate = useNavigate();

  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [slug, setSlug] = useState('');
  const [category, setCategory] = useState('');
  const [destinationTag, setDestinationTag] = useState('');
  const [publishedAt, setPublishedAt] = useState(''); // ISO date string (YYYY-MM-DD)
  const [seoTitle, setSeoTitle] = useState('');
  const [seoMetaDesc, setSeoMetaDesc] = useState('');
  const [coverImageId, setCoverImageId] = useState('');
  const [externalCoverUrl, setExternalCoverUrl] = useState('');
  const [authorName, setAuthorName] = useState('');
  const [authorRole, setAuthorRole] = useState('');
  const [scheduledPublishAt, setScheduledPublishAt] = useState(''); // ISO datetime (UTC) — backend storage
  const [status, setStatus] = useState('draft');
  const [saving, setSaving] = useState(false);

  // ── Relational spine (Phase 9a) ───────────────────────────────────────
  // Linked journeys / destinations / hotels / media + campaigns + featured.
  const [linkedJourneys, setLinkedJourneys] = useState([]);
  const [linkedDestinations, setLinkedDestinations] = useState([]);
  const [linkedHotels, setLinkedHotels] = useState([]);
  const [linkedMedia, setLinkedMedia] = useState([]);
  const [campaignIds, setCampaignIds] = useState([]);
  const [featured, setFeatured] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);

  // Auto-save state — debounced 2.5s after last edit
  const [autoSaveState, setAutoSaveState] = useState('idle'); // idle | saving | saved | error
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const autoSaveTimerRef = useRef(null);
  const entryIdRef = useRef(isNew ? null : id);

  // AI state
  const [aiBusy, setAiBusy] = useState(false);
  const [aiBusyLabel, setAiBusyLabel] = useState('');
  const [showDocMenu, setShowDocMenu] = useState(false);
  const [showSelMenu, setShowSelMenu] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [genTopic, setGenTopic] = useState('');
  const [genLength, setGenLength] = useState('standard');
  const [aiToast, setAiToast] = useState('');

  // Image dialog state
  const [imgDialog, setImgDialog] = useState(null); // null | 'cover' | 'inline'
  const [imgTab, setImgTab] = useState('url'); // 'url' | 'library'
  const [imgUrlInput, setImgUrlInput] = useState('');
  const [imgAltInput, setImgAltInput] = useState('');
  const [mediaItems, setMediaItems] = useState([]);

  // Feb 2026 — Pexels picker.  Opens from the URL tab; picked photos
  // are auto-imported into the Media Library via the helper below and
  // become the cover/inline image in one step.
  const [pexelsOpen, setPexelsOpen] = useState(false);

  const [showPreview, setShowPreview] = useState(false);
  const aiContainerRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      LinkExt.configure({ openOnClick: false }),
      Image.configure({ inline: false, allowBase64: false }),
      Placeholder.configure({
        placeholder: ({ node }) => {
          if (node.type.name === 'heading') return 'Section heading…';
          return 'Begin writing — or tap "Generate full article" above to let the assistant draft a polished piece.';
        },
      }),
    ],
    editorProps: {
      attributes: {
        class: 'vt-journal-prose prose prose-neutral max-w-none min-h-[480px] px-6 py-5 outline-none text-[#1A1A1A] text-[16px] leading-[1.75] prose-headings:font-serif prose-headings:text-[#1A1A1A] prose-headings:font-normal prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-4 prose-h2:leading-tight prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3 prose-p:my-5 prose-p:leading-[1.75] prose-blockquote:border-l-2 prose-blockquote:border-[#C9A961] prose-blockquote:pl-6 prose-blockquote:py-2 prose-blockquote:italic prose-blockquote:font-serif prose-blockquote:text-xl prose-blockquote:text-[#1A1A1A] prose-blockquote:my-10 prose-img:rounded-lg prose-img:my-8 prose-img:w-full prose-ul:my-6 prose-ol:my-6 prose-li:my-1.5',
        'data-testid': 'journal-editor-content',
      },
    },
  });

  // Close menus on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (aiContainerRef.current && !aiContainerRef.current.contains(e.target)) {
        setShowDocMenu(false);
        setShowSelMenu(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // Auto-clear toast
  useEffect(() => {
    if (!aiToast) return;
    const t = setTimeout(() => setAiToast(''), 3500);
    return () => clearTimeout(t);
  }, [aiToast]);

  useEffect(() => {
    if (!isNew && id) {
      (async () => {
        try {
          const res = await axios.get(`${API}/api/cms/journal/${id}`, { withCredentials: true });
          const d = res.data;
          setTitle(d.title || '');
          setSubtitle(d.subtitle || '');
          setSlug(d.slug || '');
          setCategory(d.category || '');
          setDestinationTag(d.destination_tag || '');
          setSeoTitle(d.seo_title || '');
          setSeoMetaDesc(d.seo_meta_description || '');
          setCoverImageId(d.cover_image_id || '');
          setExternalCoverUrl(d.external_cover_url || '');
          setAuthorName(d.author_name || '');
          setAuthorRole(d.author_role || '');
          setScheduledPublishAt(d.scheduled_publish_at || '');
          setStatus(d.status || 'draft');
          // Relational spine hydration
          setLinkedJourneys(d.linked_journey_ids || []);
          setLinkedDestinations(d.linked_destination_slugs || []);
          setLinkedHotels(d.linked_hotel_tags || []);
          setLinkedMedia(d.linked_media_ids || []);
          setCampaignIds(d.campaign_ids || []);
          setFeatured(!!d.featured);
          // Backend stores ISO datetime — pull out the YYYY-MM-DD portion for the input
          if (d.published_at) {
            setPublishedAt(String(d.published_at).slice(0, 10));
          }
          if (editor && d.content) editor.commands.setContent(d.content);
          // Reset dirty flag after initial load
          setIsDirty(false);
          if (d.updated_at) setLastSavedAt(d.updated_at);
        } catch {
          navigate('/portal/advisor/journal');
        }
      })();
    }
  }, [id, isNew, editor, navigate]);

  const autoSlug = useCallback((t) => {
    return t.toLowerCase().trim().replace(/[^\w\s-]/g, '').replace(/[\s_]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
  }, []);

  const handleTitleChange = (val) => {
    setTitle(val);
    if (isNew || slug === autoSlug(title)) setSlug(autoSlug(val));
  };

  /**
   * Build the body sent to the backend. Pulled into a helper so both manual
   * save() and the auto-save loop produce identical payloads.
   *
   * - `tags` is auto-derived server-side from category + destination_tag.
   *   The editor no longer carries a free-form tags input.
   * - `published_at` is sent as an ISO datetime only when the user supplied a
   *   YYYY-MM-DD date. Empty string lets the backend default (now() at publish).
   */
  const buildBody = (s) => {
    const normalizedContent = normalizeContent(editor?.getHTML() || '', title);
    const pubIso = publishedAt ? new Date(`${publishedAt}T12:00:00Z`).toISOString() : '';
    return {
      title,
      subtitle,
      slug,
      category,
      destination_tag: destinationTag,
      seo_title: seoTitle,
      seo_meta_description: seoMetaDesc,
      cover_image_id: coverImageId,
      external_cover_url: externalCoverUrl,
      author_name: authorName,
      author_role: authorRole,
      content: normalizedContent,
      status: s,
      published_at: pubIso,
      scheduled_publish_at: scheduledPublishAt || '',
      // Relational spine
      linked_journey_ids: linkedJourneys,
      linked_destination_slugs: linkedDestinations,
      linked_hotel_tags: linkedHotels,
      linked_media_ids: linkedMedia,
      campaign_ids: campaignIds,
      featured,
    };
  };

  const save = async (publishStatus) => {
    const s = publishStatus || status;
    setSaving(true);
    const body = buildBody(s);
    try {
      if (isNew && !entryIdRef.current) {
        const res = await axios.post(`${API}/api/cms/journal`, body, { withCredentials: true });
        entryIdRef.current = res.data.id;
        if (res.data.published_at) setPublishedAt(String(res.data.published_at).slice(0, 10));
        navigate(`/advisor/content/journal/${res.data.id}`, { replace: true });
      } else {
        const targetId = entryIdRef.current || id;
        const res = await axios.put(`${API}/api/cms/journal/${targetId}`, body, { withCredentials: true });
        if (res.data.published_at) setPublishedAt(String(res.data.published_at).slice(0, 10));
      }
      setStatus(s);
      setLastSavedAt(new Date().toISOString());
      setIsDirty(false);
      setAiToast(s === 'published' ? 'Published.' : 'Saved.');
    } catch (err) {
      console.error(err);
      setAiToast('Save failed.');
    } finally {
      setSaving(false);
    }
  };

  // ── Schedule auto-publish ──────────────────────────────────────────
  // Save the entry with status='scheduled' + scheduled_publish_at set.
  // Backend's scheduler loop will flip status→published when the time hits.
  const schedulePublish = async (isoUtc) => {
    if (!isoUtc) return;
    setSaving(true);
    const body = { ...buildBody('scheduled'), scheduled_publish_at: isoUtc };
    try {
      if (isNew && !entryIdRef.current) {
        const res = await axios.post(`${API}/api/cms/journal`, body, { withCredentials: true });
        entryIdRef.current = res.data.id;
        navigate(`/advisor/content/journal/${res.data.id}`, { replace: true });
      } else {
        await axios.put(`${API}/api/cms/journal/${entryIdRef.current || id}`, body, { withCredentials: true });
      }
      setStatus('scheduled');
      setScheduledPublishAt(isoUtc);
      setLastSavedAt(new Date().toISOString());
      setIsDirty(false);
      setShowScheduleModal(false);
      const when = new Date(isoUtc);
      setAiToast(`Scheduled for ${when.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}.`);
    } catch (e) {
      console.error(e);
      setAiToast('Schedule failed.');
    } finally {
      setSaving(false);
    }
  };

  const unschedule = async () => {
    if (!entryIdRef.current && !id) return;
    setSaving(true);
    try {
      await axios.put(`${API}/api/cms/journal/${entryIdRef.current || id}`, { scheduled_publish_at: '' }, { withCredentials: true });
      setScheduledPublishAt('');
      setStatus('draft');
      setShowScheduleModal(false);
      setAiToast('Schedule cleared.');
    } catch {
      setAiToast('Couldn\'t clear schedule.');
    } finally {
      setSaving(false);
    }
  };

  // ── Auto-save ────────────────────────────────────────────────────────
  // Debounced background save 2.5s after the user stops editing. Honors the
  // current status (drafts stay drafts, published stays published) and uses
  // the same payload builder as manual save so behaviour is identical.
  const runAutoSave = useCallback(async () => {
    if (!title.trim()) return; // never auto-save without a title
    setAutoSaveState('saving');
    const body = buildBody(status);
    try {
      if (!entryIdRef.current) {
        const res = await axios.post(`${API}/api/cms/journal`, body, { withCredentials: true });
        entryIdRef.current = res.data.id;
        // Quietly replace the URL so future autosaves PUT instead of POST
        window.history.replaceState({}, '', `/advisor/content/journal/${res.data.id}`);
      } else {
        await axios.put(`${API}/api/cms/journal/${entryIdRef.current}`, body, { withCredentials: true });
      }
      setLastSavedAt(new Date().toISOString());
      setAutoSaveState('saved');
      setIsDirty(false);
    } catch {
      setAutoSaveState('error');
    }
  }, [title, status, subtitle, slug, category, destinationTag, seoTitle, seoMetaDesc, coverImageId, externalCoverUrl, publishedAt, authorName, authorRole, scheduledPublishAt, editor, linkedJourneys, linkedDestinations, linkedHotels, linkedMedia, campaignIds, featured]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mark dirty whenever ANY tracked field changes
  useEffect(() => {
    setIsDirty(true);
  }, [title, subtitle, slug, category, destinationTag, seoTitle, seoMetaDesc, coverImageId, externalCoverUrl, publishedAt, authorName, authorRole, scheduledPublishAt, linkedJourneys, linkedDestinations, linkedHotels, linkedMedia, campaignIds, featured]);

  // Editor content changes — Tiptap fires onUpdate; we track via a polling tick
  useEffect(() => {
    if (!editor) return;
    const handler = () => setIsDirty(true);
    editor.on('update', handler);
    return () => { editor.off('update', handler); };
  }, [editor]);

  // Debounced auto-save loop
  useEffect(() => {
    if (!isDirty) return;
    if (saving) return; // avoid stomping on manual saves
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      runAutoSave();
    }, 2500);
    return () => { if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current); };
  }, [isDirty, saving, runAutoSave]);

  // ── AI: generate full article from title + topic ─────────────────────
  const runGenerateArticle = async () => {
    if (!title.trim() && !genTopic.trim()) {
      setAiToast('Add a title or a topic first.');
      return;
    }
    setShowGenerateModal(false);
    setAiBusy(true);
    setAiBusyLabel('Drafting article…');
    try {
      const res = await axios.post(`${API}/api/cms/ai/generate-article`, {
        title,
        topic: genTopic,
        destination: destinationTag,
        category,
        length: genLength,
      }, { withCredentials: true });
      if (editor && res.data?.content) {
        editor.commands.setContent(res.data.content);
        setAiToast('Article drafted. Refine as you go.');
      }
    } catch (e) {
      console.error(e);
      setAiToast('Drafting failed. Try again.');
    } finally {
      setAiBusy(false);
      setAiBusyLabel('');
    }
  };

  // ── AI: whole-document actions (write back into editor) ──────────────
  const runDocAction = async (action) => {
    if (!editor) return;
    const content = editor.getHTML();
    if (!content || content === '<p></p>') {
      setAiToast('Write something first, or use "Generate full article".');
      return;
    }
    setShowDocMenu(false);
    setAiBusy(true);
    setAiBusyLabel(`Working: ${action.replace(/_/g, ' ')}…`);
    try {
      const res = await axios.post(`${API}/api/cms/ai/action`, { content, action }, { withCredentials: true });
      const result = (res.data?.result || '').trim();
      if (!result) throw new Error('empty');

      if (action === 'add_intro') {
        // Prepend to existing doc
        editor.commands.setContent(result + content);
      } else if (action === 'add_conclusion') {
        editor.commands.setContent(content + result);
      } else {
        editor.commands.setContent(result);
      }
      setAiToast('Updated.');
    } catch (e) {
      console.error(e);
      setAiToast('AI action failed.');
    } finally {
      setAiBusy(false);
      setAiBusyLabel('');
    }
  };

  // ── AI: selection actions (replace selected range in-place) ──────────
  const runSelectionAction = async (action) => {
    if (!editor) return;
    const { from, to, empty } = editor.state.selection;
    if (empty) {
      setAiToast('Select some text first to edit just that passage.');
      setShowSelMenu(false);
      return;
    }
    const selection = editor.state.doc.textBetween(from, to, '\n');
    const context = editor.getText();
    setShowSelMenu(false);
    setAiBusy(true);
    setAiBusyLabel(`Rewriting selection…`);
    try {
      const res = await axios.post(`${API}/api/cms/ai/edit-selection`, {
        selection,
        action,
        context,
      }, { withCredentials: true });
      const result = (res.data?.result || '').trim();
      if (!result) throw new Error('empty');
      // Replace selection with returned HTML
      editor.chain().focus().deleteRange({ from, to }).insertContent(result).run();
      setAiToast('Selection rewritten.');
    } catch (e) {
      console.error(e);
      setAiToast('AI rewrite failed.');
    } finally {
      setAiBusy(false);
      setAiBusyLabel('');
    }
  };

  // ── AI: SEO meta ─────────────────────────────────────────────────────
  const runSeoMeta = async () => {
    if (!editor) return;
    const content = editor.getHTML();
    if (!content || content === '<p></p>') {
      setAiToast('Write or generate the article first.');
      return;
    }
    setAiBusy(true);
    setAiBusyLabel('Generating SEO meta…');
    try {
      const res = await axios.post(`${API}/api/cms/ai/action`, { content, action: 'seo_meta' }, { withCredentials: true });
      const raw = (res.data?.result || '').trim();
      // Try to parse JSON (may be wrapped in code fences)
      let cleaned = raw.replace(/^```(?:json)?/i, '').replace(/```$/, '').trim();
      try {
        const obj = JSON.parse(cleaned);
        if (obj.seo_title) setSeoTitle(obj.seo_title);
        if (obj.meta_description) setSeoMetaDesc(obj.meta_description);
        setAiToast('SEO meta populated.');
      } catch {
        setAiToast('Couldn\'t parse SEO response.');
      }
    } catch (e) {
      setAiToast('SEO generation failed.');
    } finally {
      setAiBusy(false);
      setAiBusyLabel('');
    }
  };

  // ── Image insertion ──────────────────────────────────────────────────
  const openImgDialog = (mode) => {
    setImgDialog(mode);
    setImgTab('url');
    setImgUrlInput('');
    setImgAltInput('');
    axios
      .get(`${API}/api/cms/media?media_type=image`, { withCredentials: true })
      .then((res) => setMediaItems(Array.isArray(res.data) ? res.data : []))
      .catch(() => setMediaItems([]));
  };

  // Detect when user pastes a Pexels DISPLAY page URL instead of the direct image URL.
  // Pexels page URLs look like: https://www.pexels.com/photo/photo-slug-123456/
  // Direct image URLs look like: https://images.pexels.com/photos/123456/pexels-photo-123456.jpeg
  const isPexelsPageUrl = (u) => /^https?:\/\/(www\.)?pexels\.com\/photo\//i.test(u);
  // Unsplash page URLs also need a hint:
  const isUnsplashPageUrl = (u) => /^https?:\/\/(www\.)?unsplash\.com\/photos\//i.test(u);

  const [imgUrlStatus, setImgUrlStatus] = useState('idle'); // idle | loading | ok | error

  // Reset status whenever URL changes
  useEffect(() => {
    if (!imgUrlInput) { setImgUrlStatus('idle'); return; }
    setImgUrlStatus('loading');
  }, [imgUrlInput]);

  // Feb 2026 — Auto-save selected external images to the Image House.
  // Quiet, fire-and-forget. The image still embeds via its source URL
  // (no UX delay), and a duplicate-safe import is kicked off in the
  // background so the Media Library accumulates the imagery the
  // advisor is actually using, organised by destination_tag.
  const autoSaveImageToHouse = useCallback(async (rawUrl, { alt = '', source = 'journal-editor' } = {}) => {
    const url = (rawUrl || '').trim();
    if (!url || !/^https?:\/\//i.test(url)) return null;
    try {
      const { data } = await axios.post(
        `${API}/api/cms/media/import-url`,
        {
          url,
          alt_text: alt || imgAltInput || title || '',
          destination_tag: destinationTag || '',
          source,
          tags: [destinationTag, category].filter(Boolean),
        },
        { withCredentials: true },
      );
      // Refresh the library tab so the freshly-imported image is
      // visible the next time the advisor opens the dialog.
      setMediaItems((prev) => {
        if (!data?.id) return prev;
        if (prev.some((m) => m.id === data.id)) return prev;
        return [data, ...prev];
      });
      return data;
    } catch (err) {
      // Soft-fail: console-only so the embed UX isn't blocked.
      // The advisor still gets the image in the article; the only
      // missing bit is the Image-House copy.
      console.warn('[image-house] import failed', err?.response?.data || err?.message || err);
      return null;
    }
  }, [destinationTag, category, imgAltInput, title]);

  const insertImageByUrl = () => {
    let url = imgUrlInput.trim();
    if (!url) return;
    // Pexels DISPLAY page URLs → auto-convert to direct CDN URL when
    // possible; only show the warning if conversion can't help.
    if (isPexelsPageUrl(url)) {
      const direct = pexelsifyUrl(url);
      if (direct && direct !== url) {
        url = direct;
      } else {
        setAiToast('That looks like the Pexels page URL. Open the photo, right-click → "Copy image address" and paste that direct .jpg/.png link instead.');
        return;
      }
    }
    if (isUnsplashPageUrl(url)) {
      setAiToast('That looks like the Unsplash page URL. Open the photo, right-click → "Copy image address" and paste that direct .jpg/.png link instead.');
      return;
    }
    // Close dialog first so editor view re-takes focus before insertion
    setImgDialog(null);
    // Fire-and-forget: copy this image into the Image House, tagged
    // with the journal's destination so it shows up in the right shelf.
    autoSaveImageToHouse(url, { source: imgDialog === 'cover' ? 'journal-cover' : 'journal-inline' });

    if (imgDialog === 'cover') {
      setExternalCoverUrl(url);
      setCoverImageId(''); // external takes priority
      setAiToast(destinationTag ? `Cover image set · saved to Image House under ${destinationTag}.` : 'Cover image set · saved to Image House.');
      return;
    }
    // Inline image — use insertContent as the most reliable path (works on
    // empty docs, after dialog close, regardless of selection position).
    // Schedule on next tick so the modal-close blur completes first.
    setTimeout(() => {
      if (!editor) return;
      const altAttr = imgAltInput ? ` alt="${imgAltInput.replace(/"/g, '&quot;')}"` : '';
      // Move cursor to end-of-doc so we APPEND (rather than replacing a
      // previously-selected image node from the last insertion).
      const endPos = editor.state.doc.content.size;
      try {
        const ok = editor
          .chain()
          .focus()
          .setTextSelection(endPos)
          .insertContent(`<p><img src="${url}"${altAttr} /></p><p></p>`)
          .run();
        if (!ok) throw new Error('insertContent returned false');
        setAiToast(destinationTag ? `Image inserted · saved to Image House under ${destinationTag}.` : 'Image inserted · saved to Image House.');
      } catch {
        editor.chain().focus('end').setImage({ src: url, alt: imgAltInput || '' }).run();
        setAiToast('Image inserted · saved to Image House.');
      }
    }, 50);
  };

  const insertImageFromLibrary = (item) => {
    setImgDialog(null);
    if (imgDialog === 'cover') {
      setCoverImageId(item.id);
      setExternalCoverUrl('');
      setAiToast('Cover image set.');
      return;
    }
    setTimeout(() => {
      if (!editor) return;
      const endPos = editor.state.doc.content.size;
      const src = `${API}${item.file_url}`;
      const alt = (item.alt_text || item.original_filename || '').replace(/"/g, '&quot;');
      editor
        .chain()
        .focus()
        .setTextSelection(endPos)
        .insertContent(`<p><img src="${src}" alt="${alt}" /></p><p></p>`)
        .run();
      setAiToast('Image inserted.');
    }, 50);
  };

  const resolvedCoverSrc = externalCoverUrl
    ? externalCoverUrl
    : coverImageId
      ? `${API}/api/cms/media/file/${coverImageId}`
      : '';

  return (
    <div className="min-h-screen bg-[#FAFAF8]" data-testid="journal-editor">
      <div className="max-w-4xl mx-auto px-6 py-10">
        {/* ── HEADER: back + actions ─────────────────────────────────── */}
        <div className="flex items-start justify-between mb-8 gap-4">
          <button
            onClick={() => navigate('/portal/advisor/journal')}
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-[#6B6B6B] hover:text-[#1A1A1A] transition-colors"
            data-testid="journal-back-btn"
            type="button"
          >
            <ArrowLeft size={14} /> Back to Journal
          </button>
          <div className="flex items-center gap-3 flex-wrap justify-end">
            {/* Auto-save indicator */}
            <span className="text-[11px] text-[#999] inline-flex items-center gap-1.5 mr-1" data-testid="autosave-indicator">
              {autoSaveState === 'saving' && <><Loader2 size={11} className="animate-spin" /> Saving…</>}
              {autoSaveState === 'saved' && lastSavedAt && <>Saved · {relativeTime(lastSavedAt)}</>}
              {autoSaveState === 'error' && <span className="text-[#C9622D]">Auto-save failed</span>}
              {autoSaveState === 'idle' && lastSavedAt && <>Saved · {relativeTime(lastSavedAt)}</>}
              {autoSaveState === 'idle' && !lastSavedAt && isDirty && <span className="opacity-60">Unsaved</span>}
            </span>
            {/* Live-on-site link — only when published */}
            {status === 'published' && slug && (
              <a
                href={`/blog/${slug}`}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-1.5 px-3 py-2 text-sm border border-[#C9A961] text-[#1A1A1A] bg-[#C9A961]/10 rounded hover:bg-[#C9A961]/20 transition-colors"
                data-testid="journal-view-live"
                title="Open the published entry on the public site"
              >
                <ExternalLink size={12} /> View on site
              </a>
            )}
            <button
              onClick={() => setShowPreview(true)}
              className="flex items-center gap-1.5 px-4 py-2 text-sm border border-[#E8E4DC] rounded hover:border-[#1A1A1A] transition-colors"
              data-testid="journal-preview-btn"
              type="button"
            >
              <Monitor size={12} /> Preview
            </button>
            <button
              onClick={() => save('draft')}
              disabled={saving}
              className="px-4 py-2 text-sm border border-[#E8E4DC] rounded hover:border-[#1A1A1A] transition-colors disabled:opacity-50"
              data-testid="journal-save-draft"
              type="button"
            >
              <Save size={12} className="inline mr-1.5" />{saving ? 'Saving…' : 'Save draft'}
            </button>
            <button
              onClick={() => setShowScheduleModal(true)}
              disabled={saving}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm border rounded transition-colors disabled:opacity-50 ${status === 'scheduled' ? 'border-[#C9A961] text-[#1A1A1A] bg-[#C9A961]/10 hover:bg-[#C9A961]/20' : 'border-[#E8E4DC] hover:border-[#1A1A1A]'}`}
              data-testid="journal-schedule-btn"
              type="button"
              title={status === 'scheduled' && scheduledPublishAt ? `Scheduled for ${new Date(scheduledPublishAt).toLocaleString()}` : 'Schedule auto-publish'}
            >
              <CalendarClock size={12} /> {status === 'scheduled' ? 'Scheduled' : 'Schedule'}
            </button>
            <button
              onClick={() => save('published')}
              disabled={saving}
              className="flex items-center gap-1.5 px-4 py-2 text-sm bg-[#1A1A1A] text-white rounded hover:bg-[#333] transition-colors disabled:opacity-50"
              data-testid="journal-publish"
              type="button"
            >
              <Eye size={12} /> {status === 'published' ? 'Update' : 'Publish'}
            </button>
          </div>
        </div>

        {/* ── TITLE + SUBTITLE ───────────────────────────────────────── */}
        <input
          value={title}
          onChange={(e) => handleTitleChange(e.target.value)}
          placeholder="Entry title"
          className="w-full font-serif text-3xl text-[#1A1A1A] bg-transparent border-none outline-none placeholder:text-[#D6D2C8] mb-2"
          data-testid="journal-title-input"
        />
        <input
          value={subtitle}
          onChange={(e) => setSubtitle(e.target.value)}
          placeholder="Subtitle / dek (optional)"
          className="w-full text-base italic text-[#6B6B6B] bg-transparent border-none outline-none placeholder:text-[#D6D2C8] mb-6 font-serif"
          data-testid="journal-subtitle-input"
        />

        {/* ── META ROW ───────────────────────────────────────────────── */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
          <input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="slug" className="text-xs px-3 py-2 border border-[#E8E4DC] rounded bg-white text-[#6B6B6B] outline-none focus:border-[#1A1A1A]" data-testid="journal-slug-input" />
          <input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Category" className="text-xs px-3 py-2 border border-[#E8E4DC] rounded bg-white text-[#6B6B6B] outline-none focus:border-[#1A1A1A]" data-testid="journal-category-input" />
          <input value={destinationTag} onChange={(e) => setDestinationTag(e.target.value)} placeholder="Destination" className="text-xs px-3 py-2 border border-[#E8E4DC] rounded bg-white text-[#6B6B6B] outline-none focus:border-[#1A1A1A]" data-testid="journal-dest-input" />
          <div className="relative">
            <input
              type="date"
              value={publishedAt}
              onChange={(e) => setPublishedAt(e.target.value)}
              className="w-full text-xs px-3 py-2 border border-[#E8E4DC] rounded bg-white text-[#6B6B6B] outline-none focus:border-[#1A1A1A]"
              data-testid="journal-publish-date"
              title="Publish date (shown on the public blog post)"
            />
          </div>
        </div>
        <p className="text-[11px] text-[#999] mb-3 -mt-1">
          Tags are auto-derived from Category and Destination. The publish date appears on the public article.
        </p>

        {/* ── Author / Writer credit ─────────────────────────────────── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
          <input
            value={authorName}
            onChange={(e) => setAuthorName(e.target.value)}
            placeholder="Writer name (e.g. Olivia Marchetti)"
            className="text-xs px-3 py-2 border border-[#E8E4DC] rounded bg-white text-[#6B6B6B] outline-none focus:border-[#1A1A1A]"
            data-testid="journal-author-name"
          />
          <input
            value={authorRole}
            onChange={(e) => setAuthorRole(e.target.value)}
            placeholder="Writer role (e.g. Editor at large)"
            className="text-xs px-3 py-2 border border-[#E8E4DC] rounded bg-white text-[#6B6B6B] outline-none focus:border-[#1A1A1A]"
            data-testid="journal-author-role"
          />
        </div>

        {/* ── COVER IMAGE PICKER ─────────────────────────────────────── */}
        <div className="mb-8">
          {resolvedCoverSrc ? (
            <div className="relative">
              <img src={resolvedCoverSrc} alt="Cover" className="w-full h-56 object-cover rounded-lg border border-[#E8E4DC]" />
              <div className="absolute top-2 right-2 flex gap-2">
                <button
                  onClick={() => openImgDialog('cover')}
                  className="px-3 py-1.5 bg-white/95 text-[11px] uppercase tracking-[0.12em] rounded border border-[#E8E4DC] hover:border-[#1A1A1A]"
                  type="button"
                  data-testid="journal-cover-replace"
                >
                  Replace
                </button>
                <button
                  onClick={() => { setCoverImageId(''); setExternalCoverUrl(''); }}
                  className="px-3 py-1.5 bg-white/95 text-[11px] uppercase tracking-[0.12em] rounded border border-[#E8E4DC] hover:border-[#1A1A1A]"
                  type="button"
                  data-testid="journal-cover-remove"
                >
                  Remove
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => openImgDialog('cover')}
              className="w-full h-32 border border-dashed border-[#E8E4DC] rounded-lg flex items-center justify-center gap-2 text-sm text-[#6B6B6B] hover:border-[#1A1A1A] hover:text-[#1A1A1A] transition-colors"
              type="button"
              data-testid="journal-cover-btn"
            >
              <ImageIcon size={16} /> Add cover image (paste URL or pick from library)
            </button>
          )}
        </div>

        {/* ── AI ASSISTANT BAR (ABOVE EDITOR) ────────────────────────── */}
        <div ref={aiContainerRef} className="bg-gradient-to-br from-[#1A1A1A] to-[#262522] text-white rounded-t-lg p-4 border border-b-0 border-[#1A1A1A]" data-testid="journal-ai-bar">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#C9A961]">
              <Sparkles size={13} /> AI editorial assistant
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {/* Generate full article */}
              <button
                onClick={() => setShowGenerateModal(true)}
                disabled={aiBusy}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-[#C9A961] text-[#1A1A1A] rounded hover:bg-[#D9B97A] disabled:opacity-50 transition-colors font-medium"
                data-testid="ai-generate-article-btn"
                type="button"
              >
                <Wand2 size={12} /> Generate full article
              </button>

              {/* Edit whole article menu */}
              <div className="relative">
                <button
                  onClick={() => { setShowDocMenu((v) => !v); setShowSelMenu(false); }}
                  disabled={aiBusy}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-white/10 text-white rounded hover:bg-white/20 disabled:opacity-50 transition-colors"
                  data-testid="ai-doc-menu-btn"
                  type="button"
                >
                  <TypeIcon size={12} /> Edit article <ChevronDown size={11} />
                </button>
                {showDocMenu && (
                  <div className="absolute top-full right-0 mt-1 bg-white text-[#1A1A1A] border border-[#E8E4DC] rounded-lg shadow-xl z-30 w-72 py-1" data-testid="ai-doc-menu">
                    {DOC_ACTIONS.map((a) => (
                      <button
                        key={a.key}
                        onClick={() => runDocAction(a.key)}
                        className="w-full text-left px-4 py-2 hover:bg-[#FAFAF8] transition-colors"
                        data-testid={`ai-doc-${a.key}`}
                        type="button"
                      >
                        <div className="text-sm">{a.label}</div>
                        <div className="text-[11px] text-[#999]">{a.hint}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Edit selection menu */}
              <div className="relative">
                <button
                  onClick={() => { setShowSelMenu((v) => !v); setShowDocMenu(false); }}
                  disabled={aiBusy}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-white/10 text-white rounded hover:bg-white/20 disabled:opacity-50 transition-colors"
                  data-testid="ai-sel-menu-btn"
                  type="button"
                >
                  <Sparkles size={12} /> Edit selection <ChevronDown size={11} />
                </button>
                {showSelMenu && (
                  <div className="absolute top-full right-0 mt-1 bg-white text-[#1A1A1A] border border-[#E8E4DC] rounded-lg shadow-xl z-30 w-56 py-1" data-testid="ai-sel-menu">
                    <div className="px-4 py-1.5 text-[10px] uppercase tracking-[0.15em] text-[#999] border-b border-[#E8E4DC]">Select text first</div>
                    {SELECTION_ACTIONS.map((a) => (
                      <button
                        key={a.key}
                        onClick={() => runSelectionAction(a.key)}
                        className="w-full text-left px-4 py-2 text-sm hover:bg-[#FAFAF8] transition-colors"
                        data-testid={`ai-sel-${a.key}`}
                        type="button"
                      >
                        {a.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* SEO meta */}
              <button
                onClick={runSeoMeta}
                disabled={aiBusy}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-white/10 text-white rounded hover:bg-white/20 disabled:opacity-50 transition-colors"
                data-testid="ai-seo-btn"
                type="button"
              >
                SEO title + meta
              </button>
            </div>
          </div>
          {aiBusy && (
            <div className="mt-3 flex items-center gap-2 text-[11px] text-white/70" data-testid="ai-busy">
              <Loader2 size={12} className="animate-spin" /> {aiBusyLabel}
            </div>
          )}
        </div>

        {/* ── EDITOR ──────────────────────────────────────────────────── */}
        <div className="bg-white border border-[#E8E4DC] rounded-b-lg overflow-hidden mb-6">
          <FormatToolbar editor={editor} onInsertImage={() => openImgDialog('inline')} />
          <EditorContent editor={editor} />
        </div>

        {/* ── CONNECTIONS PANEL (Phase 9a — relational spine) ─────────── */}
        <div className="bg-white border border-[#E8E4DC] rounded-lg p-5 mb-6" data-testid="journal-connections-panel">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <LinkIcon size={13} className="text-[#999]" strokeWidth={1.5} />
              <span className="text-[10px] uppercase tracking-[0.18em] text-[#999]">Connections</span>
            </div>
            <button
              type="button"
              onClick={() => setFeatured(v => !v)}
              data-testid="journal-toggle-featured"
              className={`inline-flex items-center gap-1.5 text-[11px] px-2.5 h-7 rounded-full border transition-colors ${
                featured
                  ? 'bg-[#C8A85A]/15 text-[#C8A85A] border-[#C8A85A]/30'
                  : 'bg-transparent text-[#999] border-[#E8E4DC] hover:text-[#1A1A1A]'
              }`}
            >
              <Star size={11} strokeWidth={1.5} />
              {featured ? 'Featured' : 'Mark featured'}
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-4">
            <div>
              <label className="text-[10px] uppercase tracking-[0.18em] text-[#999] block mb-1.5">
                Journeys
              </label>
              <RelationalPicker
                kind="journey"
                value={linkedJourneys}
                onChange={setLinkedJourneys}
                testid="journal-pick-journey"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.18em] text-[#999] block mb-1.5">
                Destinations
              </label>
              <RelationalPicker
                kind="destination"
                value={linkedDestinations}
                onChange={setLinkedDestinations}
                testid="journal-pick-destination"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.18em] text-[#999] block mb-1.5">
                Hotels <span className="opacity-60 normal-case tracking-normal">(free-text for now)</span>
              </label>
              <RelationalPicker
                kind="hotel"
                value={linkedHotels}
                onChange={setLinkedHotels}
                testid="journal-pick-hotel"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.18em] text-[#999] block mb-1.5">
                Media
              </label>
              <RelationalPicker
                kind="media"
                value={linkedMedia}
                onChange={setLinkedMedia}
                testid="journal-pick-media"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] uppercase tracking-[0.18em] text-[#999] block mb-1.5">
                Campaigns
              </label>
              <RelationalPicker
                kind="campaign"
                value={campaignIds}
                onChange={setCampaignIds}
                testid="journal-pick-campaign"
              />
            </div>
          </div>
        </div>

        {/* ── SEO FIELDS ──────────────────────────────────────────────── */}
        <div className="bg-white border border-[#E8E4DC] rounded-lg p-5 mb-6">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[10px] uppercase tracking-[0.18em] text-[#999]">Search engine preview</span>
            <button
              onClick={runSeoMeta}
              disabled={aiBusy}
              className="text-[11px] text-[#6B6B6B] hover:text-[#1A1A1A] inline-flex items-center gap-1 disabled:opacity-50"
              type="button"
              data-testid="ai-seo-regen"
            >
              <Sparkles size={11} /> Regenerate with AI
            </button>
          </div>
          <input
            value={seoTitle}
            onChange={(e) => setSeoTitle(e.target.value)}
            placeholder="SEO title (max 60 chars)"
            maxLength={80}
            className="w-full text-[15px] text-[#1A1A1A] bg-transparent border-b border-[#E8E4DC] outline-none focus:border-[#1A1A1A] py-1.5 mb-2"
            data-testid="journal-seo-title"
          />
          <textarea
            value={seoMetaDesc}
            onChange={(e) => setSeoMetaDesc(e.target.value)}
            placeholder="Meta description (max 155 chars)"
            maxLength={200}
            rows={2}
            className="w-full text-sm text-[#6B6B6B] bg-transparent border-b border-[#E8E4DC] outline-none focus:border-[#1A1A1A] py-1.5 resize-none"
            data-testid="journal-seo-meta"
          />
        </div>
      </div>

      {/* ── TOAST ─────────────────────────────────────────────────────── */}
      {aiToast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-[#1A1A1A] text-white text-sm px-4 py-2 rounded shadow-lg z-50" data-testid="journal-toast">
          {aiToast}
        </div>
      )}

      {/* ── GENERATE-ARTICLE MODAL ─────────────────────────────────────── */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6" onClick={() => setShowGenerateModal(false)}>
          <div className="bg-white rounded-lg max-w-lg w-full p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="ai-generate-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-xl text-[#1A1A1A]">Generate full article</h3>
              <button onClick={() => setShowGenerateModal(false)} className="p-1 hover:bg-[#FAFAF8] rounded" type="button">
                <X size={16} />
              </button>
            </div>
            <p className="text-xs text-[#6B6B6B] mb-4">
              The assistant will draft a fully-structured article: intro, section headings, paragraphs, a pull-quote, and conclusion — in the VÉRAULT voice.
            </p>
            <label className="block text-[11px] uppercase tracking-[0.15em] text-[#999] mb-1.5">Title</label>
            <div className="text-sm text-[#1A1A1A] mb-4 italic px-3 py-2 bg-[#FAFAF8] rounded border border-[#E8E4DC]">
              {title || <span className="text-[#999] not-italic">Type a title above first — or describe the topic below.</span>}
            </div>
            <label className="block text-[11px] uppercase tracking-[0.15em] text-[#999] mb-1.5">Topic / brief (optional)</label>
            <textarea
              value={genTopic}
              onChange={(e) => setGenTopic(e.target.value)}
              rows={3}
              placeholder="e.g. A meditation on slow travel in the Cinque Terre, focusing on shoulder season trains and family-run trattorias."
              className="w-full text-sm px-3 py-2 border border-[#E8E4DC] rounded outline-none focus:border-[#1A1A1A] mb-4 resize-none"
              data-testid="ai-gen-topic"
            />
            <label className="block text-[11px] uppercase tracking-[0.15em] text-[#999] mb-1.5">Length</label>
            <div className="flex gap-2 mb-6">
              {[
                { v: 'short', l: 'Short' },
                { v: 'standard', l: 'Standard' },
                { v: 'long', l: 'Long' },
              ].map((o) => (
                <button
                  key={o.v}
                  onClick={() => setGenLength(o.v)}
                  className={`px-3 py-1.5 text-xs rounded border transition-colors ${genLength === o.v ? 'bg-[#1A1A1A] text-white border-[#1A1A1A]' : 'border-[#E8E4DC] text-[#6B6B6B] hover:border-[#1A1A1A]'}`}
                  type="button"
                  data-testid={`ai-gen-length-${o.v}`}
                >
                  {o.l}
                </button>
              ))}
            </div>
            {(editor?.getHTML() && editor.getHTML() !== '<p></p>') && (
              <p className="text-[11px] text-[#C9622D] mb-3">
                This will replace the existing draft content.
              </p>
            )}
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowGenerateModal(false)} className="px-4 py-2 text-sm border border-[#E8E4DC] rounded hover:border-[#1A1A1A]" type="button">
                Cancel
              </button>
              <button
                onClick={runGenerateArticle}
                className="px-4 py-2 text-sm bg-[#1A1A1A] text-white rounded hover:bg-[#333] inline-flex items-center gap-1.5"
                type="button"
                data-testid="ai-gen-confirm"
              >
                <Wand2 size={12} /> Draft article
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── SCHEDULE-PUBLISH MODAL ──────────────────────────────────────── */}
      {showScheduleModal && <ScheduleModal
        initialIso={scheduledPublishAt}
        currentStatus={status}
        onClose={() => setShowScheduleModal(false)}
        onSchedule={schedulePublish}
        onUnschedule={unschedule}
        saving={saving}
      />}


      {/* ── IMAGE DIALOG (URL paste + media library) ───────────────────── */}
      {imgDialog && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6" onClick={() => setImgDialog(null)}>
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[80vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="img-dialog">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E8E4DC]">
              <h3 className="font-serif text-lg text-[#1A1A1A]">
                {imgDialog === 'cover' ? 'Cover image' : 'Insert image'}
              </h3>
              <button onClick={() => setImgDialog(null)} className="p-1 hover:bg-[#FAFAF8] rounded" type="button">
                <X size={16} />
              </button>
            </div>
            <div className="flex border-b border-[#E8E4DC]">
              <button
                onClick={() => setImgTab('url')}
                className={`px-6 py-3 text-sm transition-colors ${imgTab === 'url' ? 'text-[#1A1A1A] border-b-2 border-[#C9A961] -mb-px' : 'text-[#6B6B6B] hover:text-[#1A1A1A]'}`}
                type="button"
                data-testid="img-tab-url"
              >
                Paste URL
              </button>
              <button
                onClick={() => setImgTab('library')}
                className={`px-6 py-3 text-sm transition-colors ${imgTab === 'library' ? 'text-[#1A1A1A] border-b-2 border-[#C9A961] -mb-px' : 'text-[#6B6B6B] hover:text-[#1A1A1A]'}`}
                type="button"
                data-testid="img-tab-library"
              >
                Media library
              </button>
            </div>
            <div className="p-6 overflow-auto flex-1">
              {imgTab === 'url' ? (
                <div>
                  <p className="text-xs text-[#6B6B6B] mb-3">
                    Paste any direct image URL — or pick a photograph from Pexels with the button below. Every selected image is saved to the Image House automatically{destinationTag ? `, tagged with ${destinationTag}` : ''}.
                  </p>
                  <label className="block text-[11px] uppercase tracking-[0.15em] text-[#999] mb-1.5">Image URL</label>
                  <div className="flex gap-2 mb-2">
                    <input
                      value={imgUrlInput}
                      onChange={(e) => setImgUrlInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && imgUrlInput.trim() && imgUrlStatus !== 'error') {
                          e.preventDefault();
                          insertImageByUrl();
                        }
                      }}
                      placeholder="https://images.pexels.com/photos/…/photo.jpg"
                      autoFocus
                      className="flex-1 text-sm px-3 py-2 border border-[#E8E4DC] rounded outline-none focus:border-[#1A1A1A]"
                      data-testid="img-url-input"
                    />
                    <button
                      type="button"
                      onClick={() => setPexelsOpen(true)}
                      className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded border border-[#E8E4DC] hover:border-[#1A1A1A] text-[12px] text-[#1A1A1A] bg-white"
                      data-testid="img-url-pexels"
                      title="Pick from Pexels"
                    >
                      <ImageIcon size={14} />
                      Pick from Pexels
                    </button>
                  </div>
                  {/* Pexels/Unsplash page URL warning */}
                  {imgUrlInput && (isPexelsPageUrl(imgUrlInput.trim()) || isUnsplashPageUrl(imgUrlInput.trim())) && (
                    <div className="mb-3 px-3 py-2 rounded bg-[#FCEAE0] border border-[#F0C8A8] text-[12px] text-[#8B4513]" data-testid="img-url-page-warning">
                      That's the page URL, not the image itself. Open the photo, right-click → <strong>"Copy image address"</strong>, and paste that direct .jpg/.png link.
                    </div>
                  )}
                  {/* Preview + status */}
                  {imgUrlInput && (
                    <button
                      type="button"
                      onClick={imgUrlStatus === 'ok' ? insertImageByUrl : undefined}
                      className={`relative w-full mb-3 border rounded overflow-hidden bg-[#FAFAF8] flex items-center justify-center min-h-[80px] max-h-60 ${imgUrlStatus === 'ok' ? 'cursor-pointer border-[#E8E4DC] hover:border-[#1A1A1A]' : 'cursor-default border-[#E8E4DC]'}`}
                      data-testid="img-url-preview"
                    >
                      <img
                        src={imgUrlInput.trim()}
                        alt="preview"
                        className="max-h-60 w-auto object-contain"
                        onLoad={() => setImgUrlStatus('ok')}
                        onError={() => setImgUrlStatus('error')}
                        style={{ display: imgUrlStatus === 'error' ? 'none' : 'block' }}
                      />
                      {imgUrlStatus === 'loading' && (
                        <span className="absolute inset-0 flex items-center justify-center text-[12px] text-[#999]">
                          <Loader2 size={14} className="animate-spin mr-1.5" /> Loading preview…
                        </span>
                      )}
                      {imgUrlStatus === 'error' && (
                        <div className="px-4 py-6 text-center">
                          <p className="text-[13px] text-[#C9622D] mb-1">Couldn't load that image.</p>
                          <p className="text-[11px] text-[#999]">Check the URL is correct and points directly to an image file (e.g. ending in .jpg, .png, .webp).</p>
                        </div>
                      )}
                      {imgUrlStatus === 'ok' && (
                        <span className="absolute bottom-2 right-2 text-[10px] uppercase tracking-[0.12em] bg-white/95 text-[#1A1A1A] px-2 py-1 rounded shadow-sm">
                          Click to insert
                        </span>
                      )}
                    </button>
                  )}
                  {imgDialog === 'inline' && (
                    <>
                      <label className="block text-[11px] uppercase tracking-[0.15em] text-[#999] mb-1.5">Alt text (optional)</label>
                      <input
                        value={imgAltInput}
                        onChange={(e) => setImgAltInput(e.target.value)}
                        placeholder="Describe the image for accessibility"
                        className="w-full text-sm px-3 py-2 border border-[#E8E4DC] rounded outline-none focus:border-[#1A1A1A] mb-3"
                        data-testid="img-alt-input"
                      />
                    </>
                  )}
                  <div className="flex justify-end gap-2 sticky bottom-0 bg-white pt-2">
                    <button onClick={() => setImgDialog(null)} className="px-4 py-2 text-sm border border-[#E8E4DC] rounded hover:border-[#1A1A1A]" type="button">
                      Cancel
                    </button>
                    <button
                      onClick={insertImageByUrl}
                      disabled={!imgUrlInput.trim() || imgUrlStatus === 'error'}
                      className="px-4 py-2 text-sm bg-[#1A1A1A] text-white rounded hover:bg-[#333] disabled:opacity-50"
                      type="button"
                      data-testid="img-url-insert"
                    >
                      {imgDialog === 'cover' ? 'Use as cover' : 'Insert image'}
                    </button>
                  </div>
                </div>
              ) : mediaItems.length === 0 ? (
                <div className="text-center py-12 text-sm text-[#999]">
                  No images uploaded yet.{' '}
                  <Link to="/advisor/content/media" className="underline text-[#1A1A1A]" onClick={() => setImgDialog(null)}>
                    Upload media first
                  </Link>
                  .
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {mediaItems.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => insertImageFromLibrary(item)}
                      className="aspect-square overflow-hidden rounded border border-[#E8E4DC] hover:border-[#1A1A1A] transition-colors"
                      type="button"
                      data-testid={`img-lib-${item.id}`}
                    >
                      <img src={`${API}${item.file_url}`} alt={item.original_filename} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Feb 2026 — Pexels picker (opens from the Image dialog).
          On pick: drops the CDN URL into the dialog input AND auto-
          imports into the Image House under the destination tag. */}
      <PexelsImagePicker
        open={pexelsOpen}
        defaultQuery={destinationTag || title || ''}
        onClose={() => setPexelsOpen(false)}
        onPick={(photo) => {
          const url = photo?.url || '';
          setPexelsOpen(false);
          if (!url) return;
          setImgUrlInput(url);
          setImgUrlStatus('ok');
          if (!imgAltInput && photo?.photographer) {
            setImgAltInput(`Photograph by ${photo.photographer} on Pexels`);
          }
          // Auto-import into the Image House (fire-and-forget), tagged
          // with the destination so the library stays organised.
          autoSaveImageToHouse(url, {
            alt: photo?.photographer ? `Photograph by ${photo.photographer} on Pexels` : '',
            source: 'pexels-picker',
          });
        }}
      />

      {/* ── PREVIEW MODAL (mirrors public /blog/:slug exactly) ────────── */}
      {showPreview && (
        <div className="fixed inset-0 bg-white z-50 overflow-auto" data-testid="journal-preview-modal">
          <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-3 bg-[#1A1A1A] text-white">
            <div className="flex items-center gap-3">
              <Monitor size={14} />
              <span className="text-xs uppercase tracking-[0.15em]">Content preview — published view</span>
              {status === 'draft' && <span className="text-[10px] px-2 py-0.5 bg-white/20 rounded-full uppercase tracking-[0.1em]">Draft</span>}
            </div>
            <button onClick={() => setShowPreview(false)} className="p-1.5 hover:bg-white/10 rounded transition-colors" data-testid="journal-preview-close" type="button">
              <X size={16} />
            </button>
          </div>
          <div className="min-h-screen bg-[#FAFAF8]">
            {resolvedCoverSrc && (
              <div className="w-full h-[50vh] overflow-hidden">
                <img src={resolvedCoverSrc} alt={title} className="w-full h-full object-cover" />
              </div>
            )}
            <article className="max-w-2xl mx-auto px-6 py-16">
              <p className="text-xs uppercase tracking-[0.15em] text-[#999]">The Journal</p>
              <h1 className="font-serif text-3xl sm:text-4xl text-[#1A1A1A] mt-6 mb-2">{title || 'Untitled'}</h1>
              {subtitle && <p className="text-base text-[#6B6B6B] mb-4 italic font-serif">{subtitle}</p>}
              <div className="flex items-center gap-3 text-xs text-[#999] mb-12 flex-wrap">
                {category && <span className="px-2 py-0.5 border border-[#E8E4DC] rounded-full">{category}</span>}
                {destinationTag && <span>{destinationTag}</span>}
                <span>{new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
              </div>
              <div
                className="vt-journal-prose prose prose-neutral max-w-none text-[#1A1A1A] text-[16px] leading-[1.75]
                  prose-headings:font-serif prose-headings:text-[#1A1A1A] prose-headings:font-normal
                  prose-h2:text-2xl sm:prose-h2:text-3xl prose-h2:mt-14 prose-h2:mb-5 prose-h2:leading-tight
                  prose-h3:text-xl prose-h3:mt-10 prose-h3:mb-3
                  prose-p:text-[#1A1A1A] prose-p:my-5 prose-p:leading-[1.75]
                  prose-a:text-[#1A1A1A] prose-a:underline prose-a:decoration-[#C9A961]/40 prose-a:underline-offset-4 hover:prose-a:decoration-[#C9A961]
                  prose-strong:text-[#1A1A1A] prose-strong:font-medium
                  prose-blockquote:border-l-2 prose-blockquote:border-[#C9A961] prose-blockquote:pl-6 prose-blockquote:py-2 prose-blockquote:italic prose-blockquote:font-serif prose-blockquote:text-xl prose-blockquote:text-[#1A1A1A] prose-blockquote:my-10 prose-blockquote:leading-snug
                  prose-ul:my-6 prose-ol:my-6 prose-li:my-1.5
                  prose-img:rounded-lg prose-img:my-10 prose-img:w-full"
                dangerouslySetInnerHTML={{ __html: normalizeContent(editor?.getHTML() || '', title) || '<p class="text-[#999] italic">No content yet. Start writing in the editor — or tap "Generate full article" — to see the preview here.</p>' }}
              />
              <div className="mt-16 pt-8 border-t border-[#E8E4DC]">
                <span className="text-sm text-[#6B6B6B]">Back to The Journal</span>
              </div>
            </article>
          </div>
        </div>
      )}
    </div>
  );
}
